import { useCallback, useEffect, useMemo, useState } from "react";
import { usePoll } from "@/hooks/usePoll";
import { Link } from "react-router-dom";
import { CaretLeft, CaretRight, UsersThree, User, Warning } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { AppHeader } from "@/components/AppHeader";
import { api } from "@/lib/api";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const cellColor = (n) => {
  if (n >= 4) return "bg-red-500/30 text-red-200";
  if (n === 3) return "bg-amber-500/30 text-amber-200";
  if (n === 2) return "bg-indigo-500/25 text-indigo-200";
  return "bg-zinc-700/50 text-zinc-200";
};

export default function TeamLoad() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [rows, setRows] = useState([]);
  const [threshold, setThreshold] = useState(3);
  const ym = `${year}-${String(month + 1).padStart(2, "0")}`;

  const load = useCallback(() => {
    api
      .get("/schedule/load", { params: { month: ym } })
      .then(({ data }) => {
        setRows(data.rows || []);
        setThreshold(data.overload_threshold || 3);
      })
      .catch(() => setRows([]));
  }, [ym]);

  useEffect(() => {
    load();
  }, [load]);
  usePoll(load);

  const days = new Date(year, month + 1, 0).getDate();
  const dayList = Array.from({ length: days }, (_, i) => i + 1);
  const isoOf = (d) => `${ym}-${String(d).padStart(2, "0")}`;
  const shift = (delta) => {
    const dt = new Date(year, month + delta, 1);
    setYear(dt.getFullYear());
    setMonth(dt.getMonth());
  };
  const grandTotal = useMemo(
    () => rows.reduce((n, r) => n + r.total, 0),
    [rows],
  );
  const overloadedDays = useMemo(
    () =>
      rows.reduce(
        (n, r) => n + Object.values(r.days).filter((c) => c >= threshold).length,
        0,
      ),
    [rows, threshold],
  );

  return (
    <div className="grain min-h-screen">
      <AppHeader title="Jobs / Team Load" />
      <main className="relative z-10 pb-20 pt-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-zinc-600">
              Jobs / Team Load
            </div>
            <h1
              className="text-2xl font-bold tracking-tight sm:text-3xl"
              data-testid="team-load-title"
            >
              {MONTHS[month]} {year}
            </h1>
            <p
              className="mt-1 text-sm text-zinc-500"
              data-testid="team-load-summary"
            >
              {grandTotal} scheduled stop{grandTotal === 1 ? "" : "s"} across{" "}
              {rows.length} crew {rows.length === 1 ? "member" : "members"}
              {overloadedDays > 0 && (
                <span
                  className="font-semibold text-red-300"
                  data-testid="team-load-overloaded-summary"
                >
                  {" "}
                  · {overloadedDays} overloaded day
                  {overloadedDays === 1 ? "" : "s"}
                </span>
              )}
            </p>
          </div>
          <div className="flex gap-2">
            <Link
              to="/projects/schedule"
              data-testid="team-load-calendar-link"
              className="flex h-11 items-center rounded-md border border-zinc-700 px-4 text-xs font-semibold uppercase tracking-widest text-zinc-300 transition-colors duration-150 hover:bg-zinc-800"
            >
              Calendar
            </Link>
            <Button
              data-testid="team-load-prev"
              variant="outline"
              size="icon"
              onClick={() => shift(-1)}
              className="h-11 w-11 border-zinc-700 bg-transparent hover:bg-zinc-800"
            >
              <CaretLeft size={18} weight="bold" />
            </Button>
            <Button
              data-testid="team-load-next"
              variant="outline"
              size="icon"
              onClick={() => shift(1)}
              className="h-11 w-11 border-zinc-700 bg-transparent hover:bg-zinc-800"
            >
              <CaretRight size={18} weight="bold" />
            </Button>
          </div>
        </div>

        <div
          className="mt-6 overflow-x-auto rounded-lg border border-zinc-800 bg-zinc-900"
          data-testid="team-load-table"
        >
          {rows.length === 0 ? (
            <p
              className="p-8 text-center text-sm text-zinc-500"
              data-testid="team-load-empty"
            >
              Nothing scheduled this month.
            </p>
          ) : (
            <table className="min-w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-zinc-800 text-zinc-500">
                  <th className="sticky left-0 z-10 bg-zinc-900 px-3 py-2 text-left text-[10px] font-bold uppercase tracking-widest">
                    Crew / Team
                  </th>
                  <th className="px-2 py-2 text-center text-[10px] font-bold uppercase tracking-widest">
                    Total
                  </th>
                  {dayList.map((d) => (
                    <th
                      key={d}
                      className="px-1 py-2 text-center font-mono-data text-[10px] text-zinc-600"
                    >
                      {d}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const overDays = Object.entries(r.days).filter(
                    ([, n]) => n >= threshold,
                  );
                  return (
                  <tr
                    key={r.crew}
                    data-testid={`team-load-row-${r.crew}`}
                    className="border-b border-zinc-800/60"
                  >
                    <td className="sticky left-0 z-10 bg-zinc-900 px-3 py-2">
                      <span className="inline-flex items-center gap-1.5 text-zinc-100">
                        {r.is_team ? (
                          <UsersThree
                            size={14}
                            weight="bold"
                            className="text-indigo-300"
                          />
                        ) : (
                          <User
                            size={14}
                            weight="bold"
                            className="text-zinc-500"
                          />
                        )}
                        <span className="whitespace-nowrap font-semibold">
                          {r.label}
                        </span>
                        {overDays.length > 0 && (
                          <span
                            data-testid={`team-load-overloaded-${r.crew}`}
                            title={`Heavy days: ${overDays
                              .map(([d, n]) => `${d} (${n})`)
                              .join(", ")}`}
                            className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-red-500/50 bg-red-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-red-300"
                          >
                            <Warning size={10} weight="bold" /> Overloaded
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="px-2 py-2 text-center">
                      <span className="font-mono-data rounded-full bg-zinc-950 px-2 py-0.5 text-xs font-bold text-zinc-200">
                        {r.total}
                      </span>
                    </td>
                    {dayList.map((d) => {
                      const n = r.days[isoOf(d)] || 0;
                      return (
                        <td key={d} className="px-1 py-1 text-center">
                          {n ? (
                            <span
                              className={`inline-flex h-6 w-6 items-center justify-center rounded font-mono-data text-xs font-bold ${cellColor(n)} ${n >= threshold ? "ring-1 ring-red-400/70" : ""}`}
                              data-testid={`team-load-cell-${r.crew}-${isoOf(d)}`}
                            >
                              {n}
                            </span>
                          ) : (
                            <span className="text-zinc-800">·</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
}
